import { useState, useEffect } from 'react';
import { Box, Typography, TextField, Button, Avatar, CircularProgress } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import { ConfigDialog } from '../../dummies/ConfigDialog/ConfigDialog';
import OrganizationSetupForm from '../../dummies/OrganizationSetupForm/OrganizationSetupForm';
import { uploadImage, deleteImage } from '../ImageUploader/actions/imageUploaderActions';
import { FirestoreService } from '../../sdk/firestoreService';
import israelCities from '../../data/israelCities';

const fieldSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        fontFamily: "'Heebo', sans-serif",
        fontSize: '0.9rem',
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(30,58,95,0.35)',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#1e3a5f',
            borderWidth: 2,
        },
    },
    '& .MuiInputLabel-root': { fontFamily: "'Heebo', sans-serif", fontSize: '0.88rem' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#1e3a5f' },
};

/**
 * ProfileDialog - דיאלוג עריכת פרטי ארגון ומשתמש
 */
const ProfileDialog = ({ open, onClose, organization, user, dispatch }) => {
    // Organization fields
    const [name, setName] = useState('');
    const [selectedCity, setSelectedCity] = useState(null);
    const [street, setStreet] = useState('');
    const [houseNumber, setHouseNumber] = useState('');
    const [logoUrl, setLogoUrl] = useState('');
    const [uploading, setUploading] = useState(false);

    // User fields
    const [displayName, setDisplayName] = useState('');
    const [photoURL, setPhotoURL] = useState('');
    const [uploadingPhoto, setUploadingPhoto] = useState(false);

    const [saving, setSaving] = useState(false);
    const [validationError, setValidationError] = useState('');

    // Initialize fields when dialog opens
    useEffect(() => {
        if (open && organization) {
            setName(organization.name || '');
            setStreet(organization.address?.street || '');
            setHouseNumber(organization.address?.houseNumber || '');
            setLogoUrl(organization.logoUrl || '');

            // Find city by name or coordinates
            const city = israelCities.find(c => c.name === organization.address?.city)
                || israelCities.find(c =>
                    c.lat === organization.location?.lat && c.lng === organization.location?.lng
                )
                || null;
            setSelectedCity(city);
        }
        if (open && user) {
            setDisplayName(user.displayName || '');
            setPhotoURL(user.photoURL || '');
        }
        if (open) {
            setValidationError('');
        }
    }, [open, organization, user]);

    const country = 'ישראל';
    const isFormValid = name && selectedCity && street && houseNumber;

    // Logo handlers
    const handleLogoUpload = async (file) => {
        if (!file.type.startsWith('image/')) {
            setValidationError('יש להעלות קובץ תמונה בלבד');
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            setValidationError('גודל הקובץ חייב להיות עד 5MB');
            return;
        }
        setValidationError('');
        setUploading(true);
        try {
            const imageUrl = await uploadImage(file, 'images');
            setLogoUrl(imageUrl);
        } catch {
            setValidationError('שגיאה בהעלאת התמונה');
        } finally {
            setUploading(false);
        }
    };

    const handleLogoDelete = async () => {
        if (!logoUrl) return;
        try {
            await deleteImage(logoUrl);
            setLogoUrl('');
        } catch (err) {
            console.error('Error deleting logo:', err);
        }
    };

    // User photo handlers
    const handlePhotoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            setValidationError('יש להעלות קובץ תמונה בלבד');
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            setValidationError('גודל הקובץ חייב להיות עד 5MB');
            return;
        }
        setValidationError('');
        setUploadingPhoto(true);
        try {
            const imageUrl = await uploadImage(file, 'images');
            setPhotoURL(imageUrl);
        } catch {
            setValidationError('שגיאה בהעלאת התמונה');
        } finally {
            setUploadingPhoto(false);
        }
    };

    const handlePhotoDelete = async () => {
        if (!photoURL) return;
        try {
            await deleteImage(photoURL);
            setPhotoURL('');
        } catch (err) {
            console.error('Error deleting photo:', err);
        }
    };

    // Save
    const handleSave = async () => {
        if (!isFormValid) return;
        setSaving(true);
        setValidationError('');

        try {
            // Update organization
            const orgUpdates = {
                name,
                logoUrl,
                address: {
                    country,
                    city: selectedCity.name,
                    street,
                    houseNumber,
                },
                location: {
                    lat: selectedCity.lat,
                    lng: selectedCity.lng,
                },
            };
            await FirestoreService.updateOrganization(organization.organizationId, orgUpdates);
            dispatch({ type: 'UPDATE_ORGANIZATION_SUCCESS', payload: orgUpdates });

            // Update user
            const userUpdates = { displayName, photoURL };
            await FirestoreService.updateUser(user.userId, userUpdates);
            dispatch({ type: 'UPDATE_USER_SUCCESS', payload: userUpdates });

            onClose();
        } catch (err) {
            console.error('Error saving profile:', err);
            setValidationError('שגיאה בשמירת הנתונים');
        } finally {
            setSaving(false);
        }
    };

    // User profile tab content
    const userTabContent = (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Box sx={{ mb: 1 }}>
                <Typography sx={{
                    fontSize: '1.25rem', fontWeight: 800,
                    color: '#1e3a5f', fontFamily: "'Heebo', sans-serif",
                    lineHeight: 1.3, mb: 0.4,
                }}>
                    פרטי משתמש
                </Typography>
                <Typography sx={{
                    fontSize: '0.85rem', color: '#6b7a8d',
                    fontFamily: "'Heebo', sans-serif",
                }}>
                    עדכנו את שם התצוגה ותמונת הפרופיל שלכם
                </Typography>
            </Box>

            {/* Photo upload */}
            <Box sx={{
                display: 'flex', alignItems: 'center', gap: 2.5,
                p: 2, border: '1.5px dashed rgba(30,58,95,0.18)',
                borderRadius: '14px', bgcolor: 'rgba(30,58,95,0.02)',
            }}>
                {photoURL ? (
                    <Avatar
                        src={photoURL}
                        sx={{
                            width: 68, height: 68,
                            border: '3px solid rgba(184,134,11,0.35)',
                            boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
                        }}
                    />
                ) : (
                    <Box sx={{
                        width: 68, height: 68, borderRadius: '50%',
                        bgcolor: 'rgba(30,58,95,0.07)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <PersonIcon sx={{ fontSize: 30, color: 'rgba(30,58,95,0.28)' }} />
                    </Box>
                )}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    <Typography sx={{
                        fontSize: '0.8rem', fontWeight: 600,
                        color: '#1e3a5f', fontFamily: "'Heebo', sans-serif",
                    }}>
                        תמונת פרופיל
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            component="label"
                            variant="outlined"
                            size="small"
                            startIcon={uploadingPhoto
                                ? <CircularProgress size={13} />
                                : <CloudUploadIcon sx={{ ml: 0.5, fontSize: 15 }} />}
                            disabled={uploadingPhoto || saving}
                            sx={{
                                borderColor: 'rgba(30,58,95,0.25)', color: '#1e3a5f',
                                borderRadius: '8px', fontFamily: "'Heebo', sans-serif",
                                fontSize: '0.75rem', textTransform: 'none', px: 1.5, py: 0.55,
                                '&:hover': { borderColor: '#1e3a5f', bgcolor: 'rgba(30,58,95,0.04)' },
                            }}
                        >
                            {uploadingPhoto ? 'מעלה...' : 'העלה תמונה'}
                            <input type="file" hidden accept="image/*" onChange={handlePhotoUpload} />
                        </Button>
                        {photoURL && !uploadingPhoto && (
                            <Button
                                variant="text"
                                color="error"
                                size="small"
                                startIcon={<DeleteIcon sx={{ ml: 0.25, fontSize: 14 }} />}
                                onClick={handlePhotoDelete}
                                disabled={saving}
                                sx={{
                                    fontFamily: "'Heebo', sans-serif",
                                    fontSize: '0.73rem', textTransform: 'none',
                                }}
                            >
                                הסר
                            </Button>
                        )}
                    </Box>
                </Box>
            </Box>

            {/* Display name */}
            <Box>
                <Typography sx={{
                    fontSize: '0.8rem', fontWeight: 700,
                    color: '#1e3a5f', mb: 0.8,
                    fontFamily: "'Heebo', sans-serif",
                }}>
                    שם תצוגה
                </Typography>
                <TextField
                    fullWidth
                    size="small"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="השם שלך"
                    sx={fieldSx}
                />
            </Box>

            {/* Email (read-only) */}
            {user?.email && (
                <Box>
                    <Typography sx={{
                        fontSize: '0.8rem', fontWeight: 700,
                        color: '#1e3a5f', mb: 0.8,
                        fontFamily: "'Heebo', sans-serif",
                    }}>
                        אימייל
                    </Typography>
                    <TextField
                        fullWidth
                        size="small"
                        value={user.email}
                        disabled
                        sx={fieldSx}
                    />
                </Box>
            )}
        </Box>
    );

    return (
        <ConfigDialog
            open={open}
            onClose={onClose}
            onConfirm={handleSave}
            title="פרטים אישיים"
            tabs={[
                {
                    label: 'פרטי ארגון',
                    content: (
                        <OrganizationSetupForm
                            name={name}
                            onNameChange={setName}
                            country={country}
                            selectedCity={selectedCity}
                            onCityChange={setSelectedCity}
                            street={street}
                            onStreetChange={setStreet}
                            houseNumber={houseNumber}
                            onHouseNumberChange={setHouseNumber}
                            logoUrl={logoUrl}
                            uploading={uploading}
                            isSubmitting={saving}
                            validationError={validationError}
                            onLogoUpload={handleLogoUpload}
                            onLogoDelete={handleLogoDelete}
                            headerTitle="פרטי הארגון"
                            headerSubtitle="עדכנו את פרטי בית הכנסת"
                        />
                    ),
                },
                {
                    label: 'פרטי משתמש',
                    content: userTabContent,
                },
            ]}
            isLoading={saving}
            confirmDisabled={!isFormValid}
            width={520}
        />
    );
};

export default ProfileDialog;
